import { ProviderNote } from "../../types/ProviderNote";
import api from "./api";

const providerNotesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProviderNotes: build.query<
      ProviderNote[],
      { providerId: string; noteType?: string }
    >({
      query: ({ providerId, noteType }) =>
        `/providers/${providerId}/notes${
          noteType ? `?noteType=${noteType}` : ""
        }`,
      providesTags: (result, error, { providerId, noteType }) => [
        { type: "ProviderNotes", id: `${providerId}-${noteType || "all"}` },
      ],
    }),
    createProviderNote: build.mutation<
      ProviderNote,
      { providerId: string; noteType: string; body: string }
    >({
      query: ({ providerId, ...body }) => ({
        url: `/providers/${providerId}/notes`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { providerId }) => [
        { type: "ProviderNotes", id: `${providerId}-sender` },
        { type: "ProviderNotes", id: `${providerId}-followUp` },
        { type: "ProviderNotes", id: `${providerId}-vetting` },
        { type: "ProviderNotes", id: `${providerId}-connection` },
        { type: "ProviderNotes", id: `${providerId}-all` },
      ],
    }),
    updateProviderNote: build.mutation<
      ProviderNote,
      {
        providerId: string;
        noteId: string;
        body?: string;
        isHighlighted?: boolean;
        isBanned?: boolean;
      }
    >({
      query: ({ providerId, noteId, ...fields }) => ({
        url: `/providers/${providerId}/notes/${noteId}`,
        method: "PATCH",
        body: fields,
      }),
      invalidatesTags: (result, error, { providerId }) => [
        { type: "ProviderNotes", id: `${providerId}-sender` },
        { type: "ProviderNotes", id: `${providerId}-followUp` },
        { type: "ProviderNotes", id: `${providerId}-vetting` },
        { type: "ProviderNotes", id: `${providerId}-connection` },
        { type: "ProviderNotes", id: `${providerId}-all` },
      ],
    }),
    deleteProviderNote: build.mutation<
      { status: string },
      { providerId: string; noteId: string }
    >({
      query: ({ providerId, noteId }) => ({
        url: `/providers/${providerId}/notes/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { providerId }) => [
        { type: "ProviderNotes", id: `${providerId}-sender` },
        { type: "ProviderNotes", id: `${providerId}-followUp` },
        { type: "ProviderNotes", id: `${providerId}-vetting` },
        { type: "ProviderNotes", id: `${providerId}-connection` },
        { type: "ProviderNotes", id: `${providerId}-all` },
      ],
    }),
    uploadNoteAttachment: build.mutation<
      { uploadUrl: string; s3Key: string },
      { providerId: string; noteId: string; fileName: string }
    >({
      query: ({ providerId, noteId, fileName }) => ({
        url: `/providers/${providerId}/notes/${noteId}/attachment`,
        method: "POST",
        body: { fileName },
      }),
      invalidatesTags: (result, error, { providerId }) => [
        { type: "ProviderNotes", id: `${providerId}-sender` },
        { type: "ProviderNotes", id: `${providerId}-followUp` },
        { type: "ProviderNotes", id: `${providerId}-vetting` },
        { type: "ProviderNotes", id: `${providerId}-connection` },
        { type: "ProviderNotes", id: `${providerId}-all` },
      ],
    }),
    getNoteAttachment: build.query<
      { downloadUrl: string; fileName: string },
      { providerId: string; noteId: string }
    >({
      query: ({ providerId, noteId }) =>
        `/providers/${providerId}/notes/${noteId}/attachment`,
    }),
    deleteNoteAttachment: build.mutation<
      { status: string },
      { providerId: string; noteId: string }
    >({
      query: ({ providerId, noteId }) => ({
        url: `/providers/${providerId}/notes/${noteId}/attachment`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { providerId }) => [
        { type: "ProviderNotes", id: `${providerId}-sender` },
        { type: "ProviderNotes", id: `${providerId}-followUp` },
        { type: "ProviderNotes", id: `${providerId}-vetting` },
        { type: "ProviderNotes", id: `${providerId}-connection` },
        { type: "ProviderNotes", id: `${providerId}-all` },
      ],
    }),
    pushNotesToConnected: build.mutation<
      {
        status: string;
        created: number;
        total: number;
        failed: { id: string; error: string }[];
      },
      {
        providerId: string;
        noteType: "sender" | "followUp" | "vetting" | "connection";
        noteContent: string;
        connectionIds?: string[];
      }
    >({
      query: ({ providerId, ...body }) => ({
        url: `/providers/${providerId}/push-notes-to-connected`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: "ProviderNotes" }],
    }),
  }),
});

export const {
  useGetProviderNotesQuery,
  useCreateProviderNoteMutation,
  useUpdateProviderNoteMutation,
  useDeleteProviderNoteMutation,
  useUploadNoteAttachmentMutation,
  useGetNoteAttachmentQuery,
  useDeleteNoteAttachmentMutation,
  usePushNotesToConnectedMutation,
} = providerNotesApi;
